/**
 * FormFlu Dashboard Layout
 * 
 * The main authenticated dashboard shell with sidebar navigation.
 * This component wraps all dashboard pages.
 * 
 * HIPAA COMPLIANCE NOTES:
 * - No PHI in navigation items or breadcrumbs
 * - User display name should be truncated if too long
 * - No PHI in any error messages or console logs
 */

import { ReactNode, useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useUserDisplay } from '../../contexts';
import { TenantSelector } from './TenantSelector';
import './DashboardLayout.css';

export interface DashboardLayoutProps {
  children: ReactNode;
  title?: string;
  subtitle?: string;
}

interface NavItem {
  icon: string;
  label: string;
  href: string;
  badge?: string;
}

const mainNavItems: NavItem[] = [
  { icon: '📊', label: 'Dashboard', href: '/dashboard' },
  { icon: '📝', label: 'Forms', href: '/dashboard/forms' },
  { icon: '📬', label: 'Submissions', href: '/dashboard/submissions' },
  { icon: '📈', label: 'Analytics', href: '/dashboard/analytics' },
];

const bottomNavItems: NavItem[] = [
  { icon: '⚙️', label: 'Settings', href: '/dashboard/settings' },
  { icon: '👥', label: 'Team', href: '/dashboard/team' },
  { icon: '❓', label: 'Help', href: '/dashboard/help' },
];

export function DashboardLayout({ children, title, subtitle }: DashboardLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const userDisplay = useUserDisplay();
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuOpen && userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  // User info from auth context
  const user = userDisplay || {
    fullName: 'Guest',
    email: '',
    initials: 'G',
  };

  return (
    <div className={`dashboard ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        {/* Logo */}
        <div className="sidebar-header">
          <Link to="/dashboard" className="sidebar-logo">
            <img 
              src="/images/finalformflumainlogo.png" 
              alt="FormFlu" 
              className="sidebar-logo-img"
            />
            {!sidebarCollapsed && (
              <span className="sidebar-logo-text">FormFlu</span>
            )}
          </Link>
          <button 
            className="sidebar-collapse-btn hide-mobile"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              {sidebarCollapsed ? (
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              ) : (
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              )}
            </svg>
          </button>
        </div>

        {/* Main Navigation */}
        <nav className="sidebar-nav">
          <ul className="sidebar-nav-list">
            {mainNavItems.map((item) => (
              <li key={item.href}>
                <Link 
                  to={item.href}
                  className={`sidebar-nav-item ${isActive(item.href) ? 'active' : ''}`}
                  title={sidebarCollapsed ? item.label : undefined}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="sidebar-nav-icon">{item.icon}</span>
                  {!sidebarCollapsed && (
                    <>
                      <span className="sidebar-nav-label">{item.label}</span>
                      {item.badge && (
                        <span className="sidebar-nav-badge">{item.badge}</span>
                      )}
                    </>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom Navigation */}
        <div className="sidebar-bottom">
          <ul className="sidebar-nav-list">
            {bottomNavItems.map((item) => (
              <li key={item.href}>
                <Link 
                  to={item.href}
                  className={`sidebar-nav-item ${isActive(item.href) ? 'active' : ''}`}
                  title={sidebarCollapsed ? item.label : undefined}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="sidebar-nav-icon">{item.icon}</span>
                  {!sidebarCollapsed && (
                    <span className="sidebar-nav-label">{item.label}</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>

          {/* User Profile */}
          <div 
            ref={userMenuRef}
            className="sidebar-user" 
            onClick={() => setUserMenuOpen(!userMenuOpen)}
          >
            <div className="sidebar-user-avatar">
              {user.initials}
            </div>
            {!sidebarCollapsed && (
              <div className="sidebar-user-info">
                <span className="sidebar-user-name">{user.fullName}</span>
                <span className="sidebar-user-email">{user.email}</span>
              </div>
            )}
            
            {/* User Dropdown Menu */}
            {userMenuOpen && (
              <div className="user-dropdown" onClick={(e) => e.stopPropagation()}>
                <button 
                  className="user-dropdown-item"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setUserMenuOpen(false);
                    navigate('/dashboard/settings');
                  }}
                >
                  Profile Settings
                </button>
                <button 
                  className="user-dropdown-item danger"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setUserMenuOpen(false);
                    handleLogout();
                  }}
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="dashboard-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main Content Area */}
      <div className="dashboard-main">
        {/* Top Header */}
        <header className="dashboard-header">
          <button 
            className="dashboard-menu-btn hide-desktop"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12h18M3 6h18M3 18h18" />
            </svg>
          </button>

          {/* Page Title */}
          {title && (
            <div className="dashboard-page-title">
              <h1>{title}</h1>
              {subtitle && <p>{subtitle}</p>}
            </div>
          )}

          {/* Search (placeholder) */}
          {!title && (
            <div className="dashboard-search">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" className="search-icon">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
              <input 
                type="search" 
                placeholder="Search forms, submissions..."
                className="search-input"
              />
            </div>
          )}

          {/* Header Actions - right side */}
          <div className="dashboard-header-actions">
            <TenantSelector />
          </div>
        </header>

        {/* Page Content */}
        <main className="dashboard-content">
          {children}
        </main>
      </div>
    </div>
  );
}

export default DashboardLayout;
